import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ toasts, removeToast }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts?.map((t) => (
      <div
        key={t?.id}
        className="flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl text-sm font-medium"
        style={{
          backgroundColor: t?.type === 'success' ? '#16a34a' : t?.type === 'error' ? '#dc2626' : '#1f1f1f',
          color: '#fff',
          fontFamily: 'Inter, sans-serif',
          border: '1px solid rgba(255,255,255,0.1)',
          minWidth: '280px',
        }}
      >
        <span>{t?.type === 'success' ? '✓' : t?.type === 'error' ? '✕' : 'ℹ'}</span>
        <span>{t?.message}</span>
        <button onClick={() => removeToast(t?.id)} className="ml-auto opacity-60 hover:opacity-100">✕</button>
      </div>
    ))}
  </div>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name?.trim()?.split(' ');
  if (parts?.length >= 2) return (parts?.[0]?.charAt(0) + parts?.[1]?.charAt(0))?.toUpperCase();
  return name?.trim()?.charAt(0)?.toUpperCase();
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ─── Server Badge ─────────────────────────────────────────────────────────────
const ServerBadge = ({ name, color }) => (
  <span
    className="px-2 py-0.5 rounded text-xs font-bold"
    style={{
      backgroundColor: `${color}18`,
      color,
      border: `1px solid ${color}30`,
      fontFamily: 'Sora, sans-serif',
    }}
  >
    {name}
  </span>
);

// ─── Role Badge ───────────────────────────────────────────────────────────────
const RoleBadge = ({ role }) => {
  const { t } = useTranslation();
  let color, label;
  if (role === 'admin') {
    color = '#E5202A';
    label = t('admin.managers.roleBadgeAdmin', 'ADMIN');
  } else if (role === 'region_manager') {
    color = '#a855f7';
    label = t('admin.managers.roleBadgeRegion', 'RESP. RÉGION');
  } else {
    color = '#3b82f6';
    label = t('admin.managers.roleBadgeManager', 'MANAGER');
  }
  return (
    <span
      className="px-2 py-0.5 rounded text-xs font-bold uppercase"
      style={{
        backgroundColor: `${color}1e`,
        color,
        border: `1px solid ${color}40`,
        fontFamily: 'Sora, sans-serif',
      }}
    >
      {label}
    </span>
  );
};

// ─── Confirmation Modal ───────────────────────────────────────────────────────
const ConfirmModal = ({ message, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
    <div className="rounded-xl p-6 w-full max-w-sm" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a' }}>
      <p className="text-white text-sm mb-6" style={{ fontFamily: 'Inter, sans-serif', lineHeight: 1.6 }}>{message}</p>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm font-medium"
          style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif' }}
        >
          {t('admin.managers.cancel', 'Annuler')}
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 rounded-lg text-sm font-bold text-white"
          style={{ backgroundColor: 'var(--color-primary)', fontFamily: 'Sora, sans-serif' }}
        >
          {t('admin.managers.confirm', 'Confirmer')}
        </button>
      </div>
    </div>
  </div>
  );
};

// ─── Add User Modal ───────────────────────────────────────────────────────────
const AddUserModal = ({ servers, onClose, onSaved, addToast }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', role: 'manager', servers: [] });
  const [saving, setSaving] = useState(false);

  const toggleServer = (s) => {
    setForm((f) => ({
      ...f,
      servers: f?.servers?.includes(s?.id) ? f?.servers?.filter((x) => x !== s?.id) : [...f?.servers, s?.id],
    }));
  };

  const handleSave = async () => {
    if (!form?.name || !form?.email || !form?.password) {
      addToast(t('admin.managers.addModal.requiredFields', 'Nom, email et mot de passe sont requis'), 'error');
      return;
    }
    setSaving(true);
    try {
      // Call Edge Function to create user with service_role (email auto-confirmed)
      // Trigger handle_new_user() auto-inserts into public.users with name, role, servers
      const resp = await supabase?.functions?.invoke('create-user', {
        body: { email: form?.email, password: form?.password, name: form?.name, role: form?.role, servers: form?.servers },
      });
      // On non-2xx, supabase-js puts generic message in error and response body in error.context
      if (resp?.error) {
        let msg = resp?.error?.message;
        try {
          const body = resp?.data || (await resp?.error?.context?.json?.());
          if (body?.error) msg = body.error;
        } catch (_) {}
        throw new Error(msg || 'Erreur Edge Function');
      }
      if (resp?.data?.error) throw new Error(resp.data.error);
      if (!resp?.data?.user?.id) throw new Error('User ID manquant dans la réponse');
      const userId = resp?.data?.user?.id;

      // Update the row created by trigger with extra fields (phone, managed_servers)
      const { error: updateError } = await supabase?.from('users')?.update({
        phone: form?.phone || null,
        managed_servers: form?.servers,
      })?.eq('id', userId);
      if (updateError) throw updateError;

      // FIX 1: Auto-create manager_objectives for manager/region_manager
      if ((form?.role === 'manager' || form?.role === 'region_manager') && form?.servers?.length > 0) {
        const now = new Date();
        const currentPeriod = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}`;
        const upserts = form?.servers?.map((serverId) => ({
          manager_id: userId,
          server: serverId,
          period: currentPeriod,
          objective_diamonds: 0,
          objective_recruitment: 0,
          diamonds_m1: 0,
        }));
        await supabase?.from('manager_objectives')?.upsert(upserts, { onConflict: 'manager_id,server,period' });
      }

      addToast(t('admin.managers.addModal.userCreated', 'Utilisateur créé avec succès'), 'success');
      onSaved();
      onClose();
    } catch (err) {
      addToast('Erreur: ' + err?.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
      <div className="rounded-xl w-full max-w-md" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#1f1f1f' }}>
          <h2 className="text-white font-bold text-base" style={{ fontFamily: 'Sora, sans-serif' }}>{t('admin.managers.addModal.title', 'Ajouter un utilisateur')}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white/80 text-xl">✕</button>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Nom */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Nom</label>
            <input
              value={form?.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e?.target?.value }))}
              className="w-full px-3 py-2 rounded-lg text-base text-white outline-none"
              style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}
              placeholder="Nom complet"
            />
          </div>
          {/* Email */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Email</label>
            <input
              type="email"
              value={form?.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e?.target?.value }))}
              className="w-full px-3 py-2 rounded-lg text-base text-white outline-none"
              style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}
              placeholder="email@example.com"
            />
          </div>
          {/* Mot de passe */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Mot de passe</label>
            <input
              type="password"
              value={form?.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e?.target?.value }))}
              className="w-full px-3 py-2 rounded-lg text-base text-white outline-none"
              style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}
              placeholder="••••••••"
            />
          </div>
          {/* Téléphone */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Téléphone</label>
            <input
              value={form?.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e?.target?.value }))}
              className="w-full px-3 py-2 rounded-lg text-base text-white outline-none"
              style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}
              placeholder="+33 6 00 00 00 00"
            />
          </div>
          {/* Rôle */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Rôle</label>
            <select
              value={form?.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e?.target?.value }))}
              className="w-full px-3 py-2 rounded-lg text-base text-white outline-none"
              style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}
            >
              <option value="manager">Manager</option>
              <option value="region_manager">Responsable Région</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {/* Serveurs */}
          <div>
            <label className="block text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Serveurs</label>
            <div className="flex flex-wrap gap-2">
              {servers?.map((s) => (
                <label key={s?.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form?.servers?.includes(s?.id)}
                    onChange={() => toggleServer(s)}
                    className="rounded"
                    style={{ accentColor: s?.color || '#3b82f6' }}
                  />
                  <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif' }}>{s?.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-3 justify-end px-6 py-4 border-t" style={{ borderColor: '#1f1f1f' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium"
            style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif' }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-primary)', fontFamily: 'Sora, sans-serif' }}
          >
            {saving ? 'Création...' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Edit User Modal ──────────────────────────────────────────────────────────
const EditUserModal = ({ user, servers, onClose, onSaved, addToast }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [role, setRole] = useState(user?.role || 'manager');
  const [active, setActive] = useState(user?.active !== false);
  const [isPaid, setIsPaid] = useState(user?.is_paid || false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [managedServers, setManagedServers] = useState(
    Array.isArray(user?.managed_servers) ? user?.managed_servers : (Array.isArray(user?.servers) ? user?.servers : [])
  );
  const [responsibleServers, setResponsibleServers] = useState(
    Array.isArray(user?.responsible_servers) ? user?.responsible_servers : []
  );
  const [saving, setSaving] = useState(false);

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    if (newRole !== 'region_manager') {
      setResponsibleServers([]);
    }
  };

  const toggleManagedServer = (serverId) => {
    setManagedServers((prev) =>
      prev?.includes(serverId) ? prev?.filter((x) => x !== serverId) : [...prev, serverId]
    );
  };

  const toggleResponsibleServer = (serverId) => {
    setResponsibleServers((prev) =>
      prev?.includes(serverId) ? prev?.filter((x) => x !== serverId) : [...prev, serverId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (email !== user?.email) {
        const { error: authError } = await supabase?.auth?.admin?.updateUserById(user?.id, { email });
        if (authError) throw authError;
      }

      const { error } = await supabase?.from('users')?.update({
        name,
        phone: phone || null,
        role,
        active,
        managed_servers: managedServers,
        responsible_servers: role === 'region_manager' ? responsibleServers : [],
        servers: managedServers,
        is_paid: isPaid,
      })?.eq('id', user?.id);
      if (error) throw error;

      // FIX 1: Upsert manager_objectives for newly added servers
      if (role === 'manager' || role === 'region_manager') {
        const prevServers = Array.isArray(user?.managed_servers) ? user?.managed_servers : [];
        const addedServers = managedServers?.filter((s) => !prevServers?.includes(s));
        if (addedServers?.length > 0) {
          const now = new Date();
          const currentPeriod = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}`;
          const upserts = addedServers?.map((serverId) => ({
            manager_id: user?.id,
            server: serverId,
            period: currentPeriod,
            objective_diamonds: 0,
            objective_recruitment: 0,
            diamonds_m1: 0,
          }));
          await supabase?.from('manager_objectives')?.upsert(upserts, { onConflict: 'manager_id,server,period' });
        }
      }

      addToast(t('admin.managers.editModal.userUpdated', 'Utilisateur mis à jour'), 'success');
      onSaved();
      onClose();
    } catch (err) {
      addToast('Erreur: ' + err?.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const uid = user?.id;
      // Libérer toutes les FK vers users avant suppression
      await supabase?.from('manager_assignments')?.update({ manager_id: null })?.eq('manager_id', uid);
      await supabase?.from('manager_objectives')?.delete()?.eq('manager_id', uid);
      await supabase?.from('manager_rates')?.delete()?.eq('manager_id', uid);
      await supabase?.from('creators')?.update({ manager_id: null })?.eq('manager_id', uid);
      await supabase?.from('creator_current_month')?.update({ manager_id: null })?.eq('manager_id', uid);
      await supabase?.from('creator_daily')?.update({ manager_id: null })?.eq('manager_id', uid);
      await supabase?.from('creator_monthly')?.update({ manager_id: null })?.eq('manager_id', uid);
      await supabase?.from('creator_monthly_history')?.update({ manager_id: null })?.eq('manager_id', uid);
      await supabase?.from('group_manager_mappings')?.update({ manager_id: null })?.eq('manager_id', uid);
      await supabase?.from('notifications')?.update({ target_manager_id: null })?.eq('target_manager_id', uid);
      await supabase?.from('notifications')?.update({ sent_by: null })?.eq('sent_by', uid);
      await supabase?.from('ai_conversations')?.delete()?.eq('user_id', uid);
      await supabase?.from('data_injections')?.update({ injected_by: null })?.eq('injected_by', uid);
      await supabase?.from('cashback_payments')?.update({ paid_by_id: null })?.eq('paid_by_id', uid);
      // Supprimer le profil
      const { error } = await supabase?.from('users')?.delete()?.eq('id', uid);
      if (error) throw error;
      addToast(`${user?.name} supprimé — groupes libérés`, 'success');
      onSaved();
      onClose();
    } catch (err) {
      addToast('Erreur suppression: ' + err?.message, 'error');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
      <div className="rounded-xl w-full max-w-md" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#1f1f1f' }}>
          <h2 className="text-white font-bold text-base" style={{ fontFamily: 'Sora, sans-serif' }}>{t('admin.managers.editModal.title', 'Modifier l\'utilisateur')}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white/80 text-xl">✕</button>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Nom */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Nom</label>
            <input
              value={name}
              onChange={(e) => setName(e?.target?.value)}
              className="w-full px-3 py-2 rounded-lg text-base text-white outline-none"
              style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}
            />
          </div>
          {/* Email */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              className="w-full px-3 py-2 rounded-lg text-base text-white outline-none"
              style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}
            />
          </div>
          {/* Téléphone */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Téléphone</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e?.target?.value)}
              className="w-full px-3 py-2 rounded-lg text-base text-white outline-none"
              style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}
              placeholder="+33 6 00 00 00 00"
            />
          </div>
          {/* Rôle */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Rôle</label>
            <select
              value={role}
              onChange={(e) => handleRoleChange(e?.target?.value)}
              className="w-full px-3 py-2 rounded-lg text-base text-white outline-none"
              style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}
            >
              <option value="manager">Manager</option>
              <option value="region_manager">Responsable Région</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {/* Statut */}
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Statut</label>
            <button
              onClick={() => setActive((a) => !a)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
              style={{
                backgroundColor: active ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)',
                color: active ? '#22c55e' : 'rgba(255,255,255,0.4)',
                border: `1px solid ${active ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.1)'}`,
                fontFamily: 'Sora, sans-serif',
              }}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? '#22c55e' : 'rgba(255,255,255,0.3)' }} />
              {active ? 'Actif' : 'Inactif'}
            </button>
          </div>
          {/* Profil payeur */}
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 0',
            borderTop: '1px solid #1f1f1f',
            marginTop: '8px'
          }}>
            <label style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.7)',
              fontFamily: 'Inter, sans-serif'
            }}>
              Profil payeur
            </label>
            <div
              onClick={() => setIsPaid(p => !p)}
              style={{
                width: '44px', height: '24px', borderRadius: '12px',
                backgroundColor: isPaid ? '#22c55e' : '#2a2a2a',
                cursor: 'pointer', position: 'relative', transition: 'all 0.2s',
                border: isPaid
                  ? '1px solid rgba(34,197,94,0.5)'
                  : '1px solid #3a3a3a'
              }}
            >
              <div style={{
                width: '18px', height: '18px', borderRadius: '50%',
                backgroundColor: 'white', position: 'absolute',
                top: '2px', transition: 'all 0.2s',
                left: isPaid ? '22px' : '2px'
              }} />
            </div>
          </div>
          {/* Serveurs managés — all roles */}
          <div>
            <label className="block text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>Serveurs managés</label>
            <div className="flex flex-wrap gap-2">
              {servers?.map((s) => (
                <label key={s?.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={managedServers?.includes(s?.id)}
                    onChange={() => toggleManagedServer(s?.id)}
                    className="rounded"
                    style={{ accentColor: s?.color || '#3b82f6' }}
                  />
                  <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif' }}>{s?.name}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Serveurs responsable région — region_manager only */}
          {role === 'region_manager' && (
            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: 'rgba(168,85,247,0.8)', fontFamily: 'Inter, sans-serif' }}>Serveurs responsable région</label>
              <div className="flex flex-wrap gap-2 p-3 rounded-lg" style={{ backgroundColor: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)' }}>
                {servers?.map((s) => (
                  <label key={s?.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={responsibleServers?.includes(s?.id)}
                      onChange={() => toggleResponsibleServer(s?.id)}
                      className="rounded"
                      style={{ accentColor: '#a855f7' }}
                    />
                    <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif' }}>{s?.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Delete confirmation */}
        {confirmDelete && (
          <div className="px-6 py-3 border-t" style={{ borderColor: '#1f1f1f', backgroundColor: 'rgba(239,68,68,0.06)' }}>
            <p className="text-xs mb-3" style={{ color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>
              Supprimer <strong>{user?.name}</strong> ? Les groupes assignés seront libérés et les créateurs désassignés.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif' }}
              >
                Non, annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: '#ef4444', fontFamily: 'Sora, sans-serif' }}
              >
                {deleting ? 'Suppression...' : 'Oui, supprimer définitivement'}
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 px-6 py-4 border-t" style={{ borderColor: '#1f1f1f' }}>
          {/* Delete button (left) */}
          {!confirmDelete && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors"
              style={{ color: 'rgba(239,68,68,0.7)', border: '1px solid rgba(239,68,68,0.2)', backgroundColor: 'transparent', fontFamily: 'Inter, sans-serif' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.08)'; e.currentTarget.style.color = '#ef4444'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'rgba(239,68,68,0.7)'; }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M5.33 4V2.67a1.33 1.33 0 011.34-1.34h2.66a1.33 1.33 0 011.34 1.34V4m2 0v9.33a1.33 1.33 0 01-1.34 1.34H4.67a1.33 1.33 0 01-1.34-1.34V4h9.34z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Supprimer
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium"
            style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif' }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-primary)', fontFamily: 'Sora, sans-serif' }}
          >
            {saving ? 'Enregistrement...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const EnhancedManagersManagement = () => {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [serverFilter, setServerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payeurFilter, setPayeurFilter] = useState('tous');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);

  // Toasts
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t?.filter((x) => x?.id !== id)), 4000);
  }, []);
  const removeToast = useCallback((id) => setToasts((t) => t?.filter((x) => x?.id !== id)), []);

  const [pushUserIds, setPushUserIds] = useState(new Set());
  const [sortField, setSortField] = useState(null); // 'last_seen_at', 'created_at'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [usersResult, serversResult, pushResult] = await Promise.all([
      supabase?.from('users')?.select('id, name, email, role, servers, managed_servers, responsible_servers, phone, active, created_at, avatar_url, is_paid, last_seen_at, last_page')?.order('role')?.order('name'),
      supabase?.from('servers')?.select('id, name, color')?.order('name'),
      supabase?.from('push_subscriptions')?.select('user_id'),
    ]);
    setUsers(usersResult?.data || []);
    setServers(serversResult?.data || []);
    const pushIds = new Set((pushResult?.data || []).map(p => p.user_id));
    setPushUserIds(pushIds);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const navigate = useNavigate();

  // Server color map keyed by id
  const serverColorMap = {};
  servers?.forEach((s) => { serverColorMap[s.id] = s?.color || '#6b7280'; });

  // Filtered users
  let filtered = users?.filter((u) => {
    const term = search?.toLowerCase();
    const matchSearch = !term || u?.name?.toLowerCase()?.includes(term) || u?.email?.toLowerCase()?.includes(term);
    const matchRole = roleFilter === 'all' || u?.role === roleFilter;
    const matchServer = !serverFilter || (u?.managed_servers && u?.managed_servers?.includes(serverFilter));
    const matchStatus = statusFilter === 'all' || (statusFilter === 'active' ? u?.active !== false : u?.active === false);
    return matchSearch && matchRole && matchServer && matchStatus;
  });

  if (payeurFilter === 'payeurs') {
    filtered = filtered?.filter(u => u?.is_paid === true);
  }

  // Sort
  if (sortField) {
    filtered = [...filtered].sort((a, b) => {
      const va = a?.[sortField] || '';
      const vb = b?.[sortField] || '';
      if (!va && !vb) return 0;
      if (!va) return 1;
      if (!vb) return -1;
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }

  // Toggle active status
  const handleToggleStatus = async (user) => {
    if (user?.active !== false) {
      setConfirmDeactivate(user);
    } else {
      const { error } = await supabase?.from('users')?.update({ active: true })?.eq('id', user?.id);
      if (error) { addToast('Erreur: ' + error?.message, 'error'); return; }
      addToast('Utilisateur activé', 'success');
      fetchData();
    }
  };

  const handleConfirmDeactivate = async () => {
    const user = confirmDeactivate;
    setConfirmDeactivate(null);
    const { error } = await supabase?.from('users')?.update({ active: false })?.eq('id', user?.id);
    if (error) { addToast('Erreur: ' + error?.message, 'error'); return; }
    addToast('Utilisateur désactivé', 'success');
    fetchData();
  };

  // Reset password — generates a random temporary password via Edge Function
  const handleResetPassword = async (user) => {
    if (!confirm(t('admin.managers.confirmResetPassword', `Réinitialiser le mot de passe de ${user?.name || user?.email} ?`))) return;
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const res = await fetch(`https://oqaidhzkuzjourwbqwyr.supabase.co/functions/v1/create-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ action: 'reset_password', user_id: user?.id }),
      });
      const json = await res?.json();
      if (!res?.ok || json?.error) { addToast('Erreur: ' + (json?.error || 'Unknown'), 'error'); return; }
      // Show temporary password in a prompt so admin can copy it
      const msg = `${t('admin.managers.newTempPassword', 'Nouveau mot de passe temporaire')} :\n\n${json?.temporary_password}\n\n${t('admin.managers.communicatePassword', 'Communiquez-le au manager.')}`;
      alert(msg);
      addToast(`${t('admin.managers.passwordResetSuccess', 'MDP réinitialisé pour')} ${user?.name || user?.email}`, 'success');
    } catch (err) {
      addToast('Erreur: ' + err?.message, 'error');
    }
  };

  const SkeletonRow = () => (
    <tr>
      {[...Array(8)]?.map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: i === 0 ? '120px' : '80px' }} />
        </td>
      ))}
    </tr>
  );

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((c) => !c)} userEmail="" />
      </div>
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <AdminMobileHeader title="Managers" />
        <div className="px-6 py-6 max-w-full">

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{t('admin.managers.title', 'Gestion Utilisateurs')}</h1>
              <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                {t('admin.managers.subtitle', 'Gestion des comptes admin et managers')}
              </p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold text-white transition-colors"
              style={{ backgroundColor: 'var(--color-primary)', fontFamily: 'Sora, sans-serif' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#cc0000'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#E5202A'; }}
            >
              <span className="text-lg leading-none">+</span>
              {t('admin.managers.addUser', 'Ajouter utilisateur')}
            </button>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-wrap gap-3 mb-6">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="6" cy="6" r="4.5" stroke="white" strokeWidth="1.5" />
                <path d="M9.5 9.5l3 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                placeholder={t('admin.managers.searchPlaceholder', 'Rechercher par nom ou email...')}
                className="w-full pl-9 pr-3 py-2 rounded-lg text-base text-white outline-none"
                style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f', fontFamily: 'Inter, sans-serif' }}
              />
            </div>

            {/* Role filter */}
            <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #1f1f1f' }}>
              {[['all', t('admin.managers.roleAll', 'Tous')], ['admin', t('admin.managers.roleAdmin', 'Admin')], ['manager', t('admin.managers.roleManager', 'Manager')], ['region_manager', t('admin.managers.roleRegionManager', 'Resp. Région')]]?.map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setRoleFilter(val)}
                  className="px-3 py-2 text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: roleFilter === val ? '#1f1f1f' : '#111111',
                    color: roleFilter === val ? '#fff' : 'rgba(255,255,255,0.4)',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Server filter */}
            <select
              value={serverFilter}
              onChange={(e) => setServerFilter(e?.target?.value)}
              className="px-3 py-2 rounded-lg text-base text-white outline-none"
              style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f', fontFamily: 'Inter, sans-serif' }}
            >
              <option value="">{t('admin.managers.allServers', 'Tous les serveurs')}</option>
              {servers?.map((s) => (
                <option key={s?.id} value={s?.id}>{s?.name}</option>
              ))}
            </select>

            {/* Status filter */}
            <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #1f1f1f' }}>
              {[['all', t('admin.managers.statusAll', 'Tous')], ['active', t('admin.managers.statusActive', 'Actif')], ['inactive', t('admin.managers.statusInactive', 'Inactif')]]?.map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setStatusFilter(val)}
                  className="px-3 py-2 text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: statusFilter === val ? '#1f1f1f' : '#111111',
                    color: statusFilter === val ? '#fff' : 'rgba(255,255,255,0.4)',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Payeur filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
                Profil:
              </span>
              {['tous', 'payeurs']?.map(key => (
                <button
                  key={key}
                  onClick={() => setPayeurFilter(key)}
                  style={{
                    padding: '4px 10px', borderRadius: '6px', fontSize: '12px',
                    backgroundColor: payeurFilter === key
                      ? 'rgba(34,197,94,0.15)' : 'transparent',
                    color: payeurFilter === key
                      ? '#22c55e' : 'rgba(255,255,255,0.4)',
                    border: `1px solid ${payeurFilter === key
                      ? 'rgba(34,197,94,0.4)' : '#2a2a2a'}`,
                    cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {key === 'tous' ? t('admin.managers.profileAll', 'All') : t('admin.managers.profilePayers', 'Payers')}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #1f1f1f', backgroundColor: '#0d0d0d' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                    {[
                      { label: t('admin.managers.colName', 'Nom') },
                      { label: t('admin.managers.colRole', 'Rôle') },
                      { label: t('admin.managers.colServers', 'Serveurs') },
                      { label: t('admin.managers.colPhone', 'Téléphone') },
                      { label: t('admin.managers.colPush', 'Push') },
                      { label: t('admin.managers.colStatus', 'Statut') },
                      { label: t('admin.managers.colCreatedAt', 'Créé le'), field: 'created_at' },
                      { label: t('admin.managers.colLastSeen', 'Dernière vue'), field: 'last_seen_at' },
                      { label: t('admin.managers.colActions', 'Actions') },
                    ]?.map((col) => (
                      <th
                        key={col.label}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{ color: sortField === col.field ? '#E5202A' : 'rgba(255,255,255,0.3)', fontFamily: 'Sora, sans-serif', backgroundColor: '#0d0d0d', cursor: col.field ? 'pointer' : 'default', userSelect: 'none' }}
                        onClick={() => col.field && handleSort(col.field)}
                      >
                        {col.label}
                        {col.field && sortField === col.field && (
                          <span style={{ marginLeft: 4 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(5)]?.map((_, i) => <SkeletonRow key={i} />)
                  ) : filtered?.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-sm" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                        {t('admin.managers.noUserFound', 'Aucun utilisateur trouvé')}
                      </td>
                    </tr>
                  ) : (
                    filtered?.map((user) => {
                      const isAdmin = user?.role === 'admin';
                      const isRegionManager = user?.role === 'region_manager';
                      const avatarColor = isAdmin ? '#E5202A' : isRegionManager ? '#a855f7' : '#3b82f6';
                      const displayServers = Array.isArray(user?.managed_servers) && user?.managed_servers?.length > 0
                        ? user?.managed_servers
                        : (Array.isArray(user?.servers) ? user?.servers : []);
                      const isActive = user?.active !== false;

                      return (
                        <tr
                          key={user?.id}
                          style={{ borderBottom: '1px solid #141414' }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          {/* Nom */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div
                                className="flex-shrink-0 rounded-full flex items-center justify-center text-white font-bold text-xs"
                                style={{ width: '34px', height: '34px', backgroundColor: avatarColor, fontFamily: 'Sora, sans-serif' }}
                              >
                                {getInitials(user?.name)}
                              </div>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className="text-sm font-medium text-white" style={{ fontFamily: 'Inter, sans-serif' }}>{user?.name || '—'}</p>
                                  {user?.is_paid && (
                                    <span style={{
                                      fontSize: '10px', fontWeight: '600',
                                      padding: '2px 6px', borderRadius: '4px',
                                      backgroundColor: 'rgba(34,197,94,0.15)',
                                      color: '#22c55e',
                                    }}>PAYEUR</span>
                                  )}
                                </div>
                                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{user?.email}</p>
                              </div>
                            </div>
                          </td>
                          {/* Rôle */}
                          <td className="px-4 py-3">
                            <RoleBadge role={user?.role} />
                          </td>
                          {/* Serveurs */}
                          <td className="px-4 py-3">
                            {displayServers?.length === 0 ? (
                              <span style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>
                            ) : (
                              <div className="flex gap-1 flex-wrap">
                                {displayServers?.map((serverId) => {
                                  const server = servers?.find((s) => s?.id === serverId);
                                  return (
                                    <ServerBadge
                                      key={serverId}
                                      name={serverId}
                                      color={server?.color || serverColorMap?.[serverId] || '#6b7280'}
                                    />
                                  );
                                })}
                              </div>
                            )}
                          </td>
                          {/* Téléphone */}
                          <td className="px-4 py-3">
                            <span className="text-sm" style={{ color: user?.phone ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>
                              {user?.phone || '—'}
                            </span>
                          </td>
                          {/* Push */}
                          <td className="px-4 py-3">
                            {pushUserIds.has(user?.id) ? (
                              <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#22c55e' }}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5a4 4 0 00-4 4v2.5L1.5 10h11L11 8V5.5a4 4 0 00-4-4z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><path d="M5.5 10.5a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.3" /></svg>
                                Activé
                              </span>
                            ) : (
                              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>
                            )}
                          </td>
                          {/* Statut */}
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleToggleStatus(user)}
                              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition-colors"
                              style={{
                                backgroundColor: isActive ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.05)',
                                color: isActive ? '#22c55e' : 'rgba(255,255,255,0.35)',
                                border: `1px solid ${isActive ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.08)'}`,
                                fontFamily: 'Sora, sans-serif',
                              }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isActive ? '#22c55e' : 'rgba(255,255,255,0.3)' }} />
                              {isActive ? t('admin.managers.active', 'Actif') : t('admin.managers.inactive', 'Inactif')}
                            </button>
                          </td>
                          {/* Créé le */}
                          <td className="px-4 py-3">
                            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                              {formatDate(user?.created_at)}
                            </span>
                          </td>
                          {/* Dernière vue */}
                          <td className="px-4 py-3">
                            {user?.last_seen_at ? (
                              <div>
                                <span className="text-xs" style={{ color: (() => { const mins = Math.floor((Date.now() - new Date(user.last_seen_at).getTime()) / 60000); return mins < 5 ? '#22c55e' : mins < 60 ? '#f59e0b' : 'rgba(255,255,255,0.4)'; })(), fontFamily: 'Inter, sans-serif' }}>
                                  {(() => {
                                    const mins = Math.floor((Date.now() - new Date(user.last_seen_at).getTime()) / 60000);
                                    if (mins < 1) return '● En ligne';
                                    if (mins < 60) return `${mins}min`;
                                    const hours = Math.floor(mins / 60);
                                    if (hours < 24) return `${hours}h`;
                                    return `${Math.floor(hours / 24)}j`;
                                  })()}
                                </span>
                                {user?.last_page && (
                                  <p className="text-[10px] mt-0.5 truncate max-w-[100px]" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>
                                    {user.last_page.replace('/admin/', '').replace('/manager/', '')}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)', fontFamily: 'Inter, sans-serif' }}>—</span>
                            )}
                          </td>
                          {/* Actions */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setEditUser(user)}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                                style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif', border: '1px solid #2a2a2a' }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2a2a2a'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1f1f1f'; }}
                              >
                                {t('admin.managers.edit', 'Modifier')}
                              </button>
                              <button
                                onClick={() => handleResetPassword(user)}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                                style={{ backgroundColor: 'rgba(59,130,246,0.1)', color: '#3b82f6', fontFamily: 'Inter, sans-serif', border: '1px solid rgba(59,130,246,0.2)' }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(59,130,246,0.18)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(59,130,246,0.1)'; }}
                              >
                                {t('admin.managers.resetPassword', 'Réinitialiser MDP')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer count */}
            {!loading && (
              <div className="px-4 py-3 border-t" style={{ borderColor: '#1f1f1f' }}>
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                  {filtered?.length} utilisateur{filtered?.length !== 1 ? 's' : ''} affiché{filtered?.length !== 1 ? 's' : ''}
                  {filtered?.length !== users?.length ? ` sur ${users?.length}` : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      </main>
      {/* Modals */}
      {showAddModal && (
        <AddUserModal
          servers={servers}
          onClose={() => setShowAddModal(false)}
          onSaved={fetchData}
          addToast={addToast}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          servers={servers}
          onClose={() => setEditUser(null)}
          onSaved={fetchData}
          addToast={addToast}
        />
      )}
      {confirmDeactivate && (
        <ConfirmModal
          message={`Désactiver le compte de ${confirmDeactivate?.name} ? L'utilisateur ne pourra plus se connecter.`}
          onConfirm={handleConfirmDeactivate}
          onCancel={() => setConfirmDeactivate(null)}
        />
      )}
      {/* Toast notifications */}
      <Toast toasts={toasts} removeToast={removeToast} />
      <AdminBottomNav />
    </div>
  );
};

export default EnhancedManagersManagement;
